/**
 * bridge/form_controller.js — "form" bridge component (Masilotti Form pattern).
 *
 * Stimulus identifier: bridge--form (glob-derived from the bridge/ subdir).
 * Strada component name: "form" (must equal the iOS FormComponent.name).
 *
 * Replaces a web form's submit <button> with a native nav-bar submit button.
 * On connect it sends the button's title (and optional hex colour) to native;
 * when native taps its button, the reply callback clicks the hidden HTML submit.
 * The turbo:submit-start / turbo:submit-end actions disable/enable both the HTML
 * button and the native button so the in-flight states stay in sync.
 *
 * As a BridgeComponent it only connect()s inside a Hotwire Native shell (gated on
 * the "Hotwire Native" UA token). In the PWA / desktop browser it never connects,
 * so the native nav button never appears and the plain HTML submit button is used.
 * On the current Android shell there is no Strada bridge, so this likewise no-ops
 * and the web submit button is used (ios-chrome tier: Android stays web-rendered).
 *
 * iOS counterpart: native/ios/Chippie/Bridge/FormComponent.swift
 * Template wiring:
 *   <form data-controller="bridge--form"
 *         data-action="turbo:submit-start->bridge--form#submitStart
 *                      turbo:submit-end->bridge--form#submitEnd">
 *     <button type="submit" data-bridge--form-target="submit"
 *             data-bridge-title="Submit">Submit form</button>
 *   </form>
 */
import { BridgeComponent, BridgeElement } from "@hotwired/hotwire-native-bridge";

export default class extends BridgeComponent {
    static component = "form";
    static targets = ["submit"];

    connect() {
        super.connect();
        this.#addButton();
    }

    disconnect() {
        super.disconnect();
        this.send("disconnect");
    }

    submitStart() {
        this.submitTarget.disabled = true;
        this.send("disableSubmit");
    }

    submitEnd() {
        this.submitTarget.disabled = false;
        this.send("enableSubmit");
    }

    #addButton() {
        const submit = new BridgeElement(this.submitTarget);
        const color = this.bridgeElement.bridgeAttribute("color");

        this.send("connect", { title: submit.title, color }, () => {
            this.submitTarget.click();
        });
    }
}
